import { ComponentProps, MouseEvent, ReactElement, ReactNode, useCallback, KeyboardEvent } from 'react';

import { twMerge } from 'tailwind-merge';

import { SelectModalItemBase } from './SelectModalItemBase';
import { SelectModalItemDefaultChecker } from './SelectModalItemChecker';

type CustomSelectModalItemBaseProps<T, Event extends MouseEvent> = ComponentProps<typeof SelectModalItemBase<T, Event>>;

type InjectedProps<TD, Event extends MouseEvent> = ComponentProps<
  Exclude<CustomSelectModalItemBaseProps<TD, Event>['children'], ReactNode>
>;

interface SelectItemDefaultCommonProps<T, Event extends MouseEvent>
  extends Pick<CustomSelectModalItemBaseProps<T, Event>, 'item' | 'isSelected' | 'isDisabled' | 'isHidden'> {
  readonly asRadio?: boolean;
  readonly className?: string;
}

interface SelectItemDefaultContentCommonProps {
  readonly asRadio?: boolean;
  readonly className?: string;
}

interface SelectItemDefaultContentWrappedProps {
  readonly unwrap?: never;
  readonly children?: ReactNode;
}

interface SelectItemDefaultContentUnwrappedProps<T, Event extends MouseEvent> {
  readonly unwrap: true;
  readonly children: (props: InjectedProps<T, Event> & SelectItemDefaultContentCommonProps) => ReactElement | null;
}

type SelectItemDefaultContentProps<T, Event extends MouseEvent> = SelectItemDefaultContentCommonProps &
  (SelectItemDefaultContentUnwrappedProps<T, Event> | SelectItemDefaultContentWrappedProps);

type SelectItemDefaultProps<T, Event extends MouseEvent> = SelectItemDefaultCommonProps<T, Event> &
  (SelectItemDefaultContentUnwrappedProps<T, Event> | SelectItemDefaultContentWrappedProps);

function getClassName(selected?: boolean, disabled?: boolean, updating?: boolean, className?: string) {
  return twMerge(
    'my-[1px] flex flex-row',
    'transition',
    'cursor-pointer px-10 py-3.5',
    'text-sm text-neutral-500',
    'hover:bg-light-blue-50 hover:text-neutral-700',
    selected && 'bg-light-blue-50 text-neutral-700',
    disabled && 'pointer-events-none bg-neutral-200 hover:cursor-not-allowed hover:bg-inherit hover:text-neutral-500',
    updating && 'pointer-events-none opacity-50',
    className,
  );
}

function SelectModalItemDefaultContent<T, Event extends MouseEvent<HTMLDivElement>>({
  asRadio,
  selected,
  disabled,
  updating,
  hidden,
  unwrap,
  item,
  children,
  className,
  onClick,
}: SelectItemDefaultContentProps<T, Event> & InjectedProps<T, Event>) {
  const onKeyUpHandler = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter') {
        onClick();
      }
    },
    [onClick],
  );

  if (unwrap) {
    return children({
      asRadio,
      selected,
      disabled,
      updating,
      hidden,
      item,
      onClick,
      className: getClassName(selected, disabled, updating, className),
    });
  }

  return hidden ? null : (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyUp={onKeyUpHandler}
      className={getClassName(selected, disabled, updating, className)}
    >
      {children}
      <div className="flex flex-auto" />
      <div className="flex flex-auto text-light-blue-50" />
      <SelectModalItemDefaultChecker asRadio={asRadio} />
    </div>
  );
}

function SelectModalItemDefault<T, Event extends MouseEvent>({
  item,
  asRadio,
  unwrap,
  isSelected,
  isDisabled,
  isHidden,
  children,
  className,
}: SelectItemDefaultProps<T, Event>) {
  const Content = SelectModalItemDefaultContent as React.FunctionComponent<
    Omit<ComponentProps<typeof SelectModalItemDefaultContent>, keyof InjectedProps<T, Event>>
  >;

  return (
    <SelectModalItemBase asChild isSelected={isSelected} isDisabled={isDisabled} isHidden={isHidden} item={item}>
      <Content asRadio={asRadio} unwrap={unwrap as undefined} className={className}>
        {children as SelectItemDefaultContentWrappedProps['children']}
      </Content>
    </SelectModalItemBase>
  );
}

export { SelectModalItemDefault };
